import { useContext, useMemo, useState } from 'react';

import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Fade,
  Paper
} from '@mui/material';
import { DateTimeField, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { useQueryClient } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import enGB from 'date-fns/locale/en-GB';

import Alert from '@/components/alert';
import Button from '@/components/button';
import Card from '@/components/card';
import Dialog from '@/components/dialog';
import { ViolationContext } from '@/contexts/ViolationContext';
import {
  useAddViolationContact,
  useDeleteViolationContact,
  useUpdateViolationContact
} from '@/hooks/queries/violations';
import { useAppSelector } from '@/hooks/redux';
import useAlert from '@/hooks/useAlert';
import { Violations } from '@/models/violations';
import { formatDate } from '@/utils/datesAndTime';
import { getPrettyDateWithTime } from '@/utils/datesAndTime/datesAndTime';

const ContactItem = ({
  contact,
  isEditable,
  onEdit,
  onDelete
}: {
  contact: Violations.Contacted;
  isEditable: boolean;
  onEdit: (contact: Violations.Contacted) => void;
  onDelete: (contact: Violations.Contacted) => void;
}): React.JSX.Element => {
  const contactDate =
    contact.contactedAt instanceof Date
      ? contact.contactedAt
      : parseISO(contact.contactedAt);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s',
        backgroundColor: isEditable ? 'background.paper' : 'grey.100',
        minWidth: 'fit-content',
        '&:hover': isEditable
          ? {
              boxShadow: 1,
              borderColor: 'primary.light'
            }
          : undefined
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        sx={{ overflowX: 'auto' }}
      >
        <Stack spacing={0.5} minWidth={150}>
          <Typography variant="body2" color="text.secondary">
            Date
          </Typography>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {getPrettyDateWithTime(contactDate)}
          </Typography>
        </Stack>

        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            Author
          </Typography>
          <Typography variant="body2">{contact.author}</Typography>
        </Stack>

        {isEditable && (
          <Stack direction="row" flexWrap="wrap" justifyContent="flex-end">
            <Tooltip title="Edit contact">
              <IconButton
                size="small"
                color="primary"
                onClick={() => onEdit(contact)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete contact">
              <IconButton size="small" onClick={() => onDelete(contact)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

const ContactFormDialog = ({
  open,
  onClose,
  onSave,
  selectedContact,
  actionText,
  title,
  isUpdating
}: {
  open: boolean;
  onClose: () => void;
  onSave: any;
  selectedContact?: Violations.Contacted;
  actionText: string;
  title: string;
  isUpdating: boolean;
}) => {
  const [newContactDate, setNewContactDate] = useState<Date | null>(
    typeof selectedContact?.contactedAt === 'string'
      ? parseISO(selectedContact.contactedAt)
      : selectedContact?.contactedAt || null
  );
  const user = useAppSelector((state) => state.user.currentUser);

  const handleSave = () => {
    if (newContactDate) {
      onSave({
        ...selectedContact,
        contactedAt: newContactDate
      });
    }
  };

  const isContactUpdated = useMemo(
    () =>
      newContactDate?.getTime() !==
      new Date(selectedContact?.contactedAt || '').getTime(),
    [title, newContactDate, selectedContact]
  );

  const isContactDateInvalid = useMemo(
    () => !newContactDate || Number.isNaN(new Date(newContactDate).getTime()),
    [newContactDate]
  );

  const isSaveButtonDisabled = useMemo(
    () =>
      title === 'Edit contact'
        ? !isContactUpdated || isContactDateInvalid
        : isContactDateInvalid,
    [title, isContactUpdated, isContactDateInvalid]
  );

  return (
    <Dialog
      fullWidth
      TransitionComponent={Fade}
      title={title}
      open={open}
      onClose={onClose}
      dialogContent={
        <Box sx={{ pt: 1 }}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={enGB}
          >
            <DateTimeField
              label="Contact date and time"
              value={newContactDate}
              onChange={setNewContactDate}
              fullWidth
              sx={{ mb: 2 }}
            />
          </LocalizationProvider>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Author
          </Typography>
          <Typography variant="body1">{user.name}</Typography>
        </Box>
      }
      actions={
        <>
          <Button variant="text" text="Cancel" onClick={onClose} />
          <Button
            loading={isUpdating}
            text={actionText}
            onClick={handleSave}
            disabled={isSaveButtonDisabled}
          />
        </>
      }
    />
  );
};

const DeleteConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  contactDate,
  isDeleting
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  contactDate: string;
  isDeleting: boolean;
}) => (
  <Dialog
    fullWidth
    TransitionComponent={Fade}
    title="Confirm deletion"
    open={open}
    dialogContent={
      <>
        <Alert
          sx={{ mb: 2 }}
          severity="warning"
          color="warning"
          content="This action cannot be undone."
        />
        <Typography>
          Are you sure you want to delete the contact from{' '}
          <Typography component="span" fontWeight={600}>
            {contactDate}
          </Typography>
          ?
        </Typography>
      </>
    }
    actions={
      <>
        <Button variant="text" text="Cancel" onClick={onClose} />
        <Button
          color="error"
          text="Delete"
          loading={isDeleting}
          onClick={onConfirm}
        />
      </>
    }
  />
);

const ViolationContact = () => {
  const alert = useAlert();
  const user = useAppSelector((state) => state.user.currentUser);
  const { postId, violationData, urlParams } = useContext(ViolationContext);
  const queryClient = useQueryClient();

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [selectedContact, setSelectedContact] = useState<Violations.Contacted>({
    contactId: 0,
    contactedAt: new Date().toISOString(),
    author: user.name
  });

  const { mutate: addContact, isPending: isAdding } = useAddViolationContact({
    onSuccess: (data) => {
      queryClient.setQueryData(
        ['violation', postId, urlParams],
        (oldData: Violations.Details) => ({
          ...oldData,
          contacts: [
            ...oldData.contacts,
            {
              ...data,
              author: user.name
            }
          ],
          timeline: [
            ...oldData.timeline,
            {
              type: 'contact',
              author: user.name,
              content: data.contactedAt,
              createdAt: new Date().toISOString()
            }
          ]
        })
      );
      alert.success('The contact was successfully added');
      setAddDialogOpen(false);
    },
    onError: () => {
      alert.error('Some error occurred while adding contact');
    }
  });

  const { mutate: updateContact, isPending: isUpdating } =
    useUpdateViolationContact({
      onSuccess: (data) => {
        queryClient.setQueryData(
          ['violation', postId, urlParams],
          (oldData: Violations.Details) => ({
            ...oldData,
            contacts: oldData.contacts.map((contact) =>
              contact.contactId === data.contactId
                ? {
                    ...contact,
                    contactedAt: data.contactedAt
                  }
                : contact
            ),
            timeline: oldData.timeline.map((timelineItem) =>
              timelineItem.type === 'contact' &&
              timelineItem.author === user.name &&
              timelineItem.content === selectedContact.contactedAt
                ? {
                    ...timelineItem,
                    content: data.contactedAt
                  }
                : timelineItem
            )
          })
        );
        alert.success('The contact was successfully updated');
        setEditDialogOpen(false);
      },
      onError: () => {
        alert.error('Some error occurred while updating contact');
      }
    });

  const { mutate: deleteContact, isPending: isDeleting } =
    useDeleteViolationContact({
      onSuccess: () => {
        queryClient.setQueryData(
          ['violation', postId, urlParams],
          (oldData: Violations.Details) => ({
            ...oldData,
            contacts: oldData.contacts.filter(
              (contact) => contact.contactId !== selectedContact.contactId
            ),
            timeline: oldData.timeline.filter(
              (timelineItem) =>
                timelineItem.type !== 'contact' ||
                timelineItem.author !== user.name ||
                timelineItem.content !== selectedContact.contactedAt
            )
          })
        );
        alert.success('The contact was successfully deleted');
        setDeleteDialogOpen(false);
      },
      onError: () => {
        alert.error('Some error occurred while deleting contact');
      }
    });

  const handleAddClick = () => {
    setAddDialogOpen(true);
  };

  const handleEditClick = (contact: Violations.Contacted) => {
    setSelectedContact(contact);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (contact: Violations.Contacted) => {
    setSelectedContact(contact);
    setDeleteDialogOpen(true);
  };

  const handleSaveNewContact = (newContact: Violations.Contacted) => {
    addContact({
      postId,
      data: {
        contactedAt: newContact.contactedAt
      }
    });
  };

  const handleUpdateContact = (updatedContact: Violations.Contacted) => {
    updateContact({
      postId,
      contactId: updatedContact.contactId,
      data: {
        contactedAt: updatedContact.contactedAt
      }
    });
  };

  const handleDeleteContact = () => {
    deleteContact({
      postId,
      contactId: selectedContact.contactId
    });
  };

  const hasContacts = useMemo(
    () => (violationData?.contacts ?? []).length > 0,
    [violationData]
  );

  if (!violationData)
    return <Typography variant="body2">Data not available</Typography>;

  if (violationData.isExpired) return <></>;

  return (
    <Card
      sx={{ marginBottom: 2 }}
      isCollapsible
      title="Contact"
      content={
        <Box>
          {!hasContacts ? (
            <Alert
              severity="info"
              color="secondary"
              content="No contact recorded yet. Add the date and time of your contact with this creator to track communication about this violation."
            />
          ) : (
            <Box
              sx={{
                maxHeight: '300px',
                overflowY: 'auto',
                scrollbarWidth: 'thin'
              }}
            >
              <Stack spacing={1}>
                {violationData.contacts.map((contact) => (
                  <ContactItem
                    key={contact.contactId}
                    contact={contact}
                    isEditable={user.name === contact.author}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteClick}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {addDialogOpen && (
            <ContactFormDialog
              open={addDialogOpen}
              onClose={() => setAddDialogOpen(false)}
              onSave={handleSaveNewContact}
              selectedContact={selectedContact}
              title="Add contact"
              actionText="confirm"
              isUpdating={isAdding}
            />
          )}

          {editDialogOpen && (
            <ContactFormDialog
              open={editDialogOpen}
              onClose={() => setEditDialogOpen(false)}
              onSave={handleUpdateContact}
              title="Edit contact"
              actionText="Update contact"
              selectedContact={selectedContact}
              isUpdating={isUpdating}
            />
          )}

          {deleteDialogOpen && (
            <DeleteConfirmDialog
              open={deleteDialogOpen}
              onClose={() => setDeleteDialogOpen(false)}
              onConfirm={handleDeleteContact}
              contactDate={
                formatDate(selectedContact.contactedAt, 'dd-MM-yyyy HH:mm') ||
                ''
              }
              isDeleting={isDeleting}
            />
          )}
        </Box>
      }
      actions={
        <Button size="small" text="Add Contact" onClick={handleAddClick} />
      }
    />
  );
};

export default ViolationContact;
